import { CandidateStatus, CandidateSummary, CandidateStatusCounts, VALID_STATUS_TRANSITIONS } from '@recruitai/shared';

/** Colunas visíveis do Kanban (exclui 'analyzing') */
export const KANBAN_COLUMNS: { status: CandidateStatus; label: string; color: string }[] = [
  { status: CandidateStatus.PENDING, label: 'Pendente', color: 'bg-yellow-100' },
  { status: CandidateStatus.APPROVED, label: 'Aprovado', color: 'bg-green-100' },
  { status: CandidateStatus.INTERVIEWED, label: 'Entrevistado', color: 'bg-blue-100' },
  { status: CandidateStatus.HIRED, label: 'Contratado', color: 'bg-emerald-100' },
  { status: CandidateStatus.REJECTED, label: 'Rejeitado', color: 'bg-red-100' },
];

const KANBAN_STATUSES = KANBAN_COLUMNS.map((c) => c.status);

/** Agrupa candidatos por status, exclui 'analyzing', ordena por fitScore desc */
export function groupCandidatesByStatus(
  candidates: CandidateSummary[]
): Record<CandidateStatus, CandidateSummary[]> {
  const groups = {} as Record<CandidateStatus, CandidateSummary[]>;
  for (const s of KANBAN_STATUSES) {
    groups[s] = [];
  }
  for (const c of candidates) {
    if (c.status === CandidateStatus.ANALYZING) continue;
    if (groups[c.status]) {
      groups[c.status].push(c);
    }
  }
  // Ordenar por fitScore decrescente dentro de cada grupo
  for (const s of KANBAN_STATUSES) {
    groups[s].sort((a, b) => (b.fitScore ?? 0) - (a.fitScore ?? 0));
  }
  return groups;
}

/** Retorna classe de cor TailwindCSS para o fitScore */
export function getScoreColor(fitScore: number | undefined | null): string {
  if (fitScore === undefined || fitScore === null) return 'text-gray-400';
  if (fitScore >= 70) return 'text-green-600';
  if (fitScore >= 40) return 'text-yellow-600';
  return 'text-red-600';
}

/** Retorna status de destino válidos para drag, excluindo 'analyzing' */
export function getValidTargets(sourceStatus: CandidateStatus): CandidateStatus[] {
  const transitions = VALID_STATUS_TRANSITIONS[sourceStatus] || [];
  return transitions.filter((s) => s !== CandidateStatus.ANALYZING);
}

/** Próximo status na sequência do pipeline */
export function getNextStatus(currentStatus: CandidateStatus): CandidateStatus | null {
  const sequence: Record<string, CandidateStatus> = {
    [CandidateStatus.PENDING]: CandidateStatus.APPROVED,
    [CandidateStatus.APPROVED]: CandidateStatus.INTERVIEWED,
    [CandidateStatus.INTERVIEWED]: CandidateStatus.HIRED,
  };
  return sequence[currentStatus] ?? null;
}

/** Verifica se o status permite transição para rejected */
export function canReject(currentStatus: CandidateStatus): boolean {
  return getValidTargets(currentStatus).includes(CandidateStatus.REJECTED);
}

/** Calcula contadores a partir dos grupos */
export function countsFromGroups(
  groups: Record<CandidateStatus, CandidateSummary[]>
): CandidateStatusCounts {
  const counts = {} as CandidateStatusCounts;
  for (const s of Object.values(CandidateStatus)) {
    counts[s] = groups[s]?.length ?? 0;
  }
  return counts;
}
